// TODO: add shallow pagination and squeantial linking
// https://developers.google.com/search/docs/specialty/ecommerce/pagination-and-incremental-page-loading#sequentially
import React, {
  useEffect, useMemo, useState, useCallback, useRef,
} from 'react';
import { useQuery } from '@apollo/client';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import GET_ALL_COURSES from '../../gql/queries/get-all-courses.gql';
import { clearUserSession, domainBaseUrl } from '../../utils/helpers';
import Spinner from '../../icons/spinner';
import NoDataFound from '../shared/NoDataFound';
import useDebounce from '../../hooks/useDebounce';
import { trackEvent } from '../../utils/analytics';
import EventNames from '../../utils/event-name-constants';
import CardSkeleton from '../shared/CardSkeleton';

function CourseList({
  isRefetch, queryPareams, queryPareamsTag = '', clearFilter, searchKey, clearFilterafterSeacrh, setSelectedTag, searchLoad,
  onPageFilters, onPageTags, type, pageType, user,
}) {
  const {
    mode, centreId, centerId, examId, language, streamId,
  } = queryPareams;

  const [loadingElement, setLoadingElement] = useState(null);
  const filterArray = useMemo(
    () => [
      // { key: 'courseScope', value: mode || '' },
      { key: 'mode', value: mode || '' },
      { key: 'centreId', value: centreId || '' },
      { key: 'tagId', value: queryPareamsTag || '' },
      { key: 'examId', value: examId || '' },
      { key: 'language', value: language || '' },
      { key: 'streamId', value: streamId || '' },
    ],
    [mode, centreId, centerId, examId, language, streamId, queryPareamsTag],
  );

  const [filters, setFilters] = useState(filterArray);
  const [currentPage, setCurrentPage] = useState(1);
  const [allCourses, setAllCourses] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastFiltersString, setLastFiltersString] = useState('');
  const observerRef = useRef();
  const { COURSE_CARD_CLICKED, COURSE_LIST_VIEWED, COURSES_SEARCH_INITIATED } = EventNames;
  const router = useRouter();
  const { mode: queryMode } = router.query;

  // Initialize filters when component mounts or when filterArray changes initially
  useEffect(() => {
    const currentFiltersString = JSON.stringify(filterArray);
    if (lastFiltersString === '') {
      setFilters(filterArray);
      setLastFiltersString(currentFiltersString);
    }
  }, [filterArray, lastFiltersString]);

  useEffect(() => {
    const courseType = {
      Online: 'online',
      Offline: 'offline',
    }[queryMode] || 'all';
    trackEvent(COURSE_LIST_VIEWED, { courseType });
  }, []);

  const {
    data: CoursesData,
    error,
    loading,
    refetch,
    fetchMore,
  } = useQuery(GET_ALL_COURSES, {
    variables: {
      filters, page: 1, limit: 12, type, pageType,
    },
    onCompleted: (data) => {
      if (data?.getCourseList?.courses) {
        setAllCourses(data.getCourseList.courses);
        setCurrentPage(1);
      }
    },
  });

  const hasNextPage = CoursesData?.getCourseList?.hasNextPage || false;
  const debouncedSearchKey = useDebounce(searchKey, 2000);
  const lastPageFiltersStringRef = useRef('');
  const lastPageTagsStringRef = useRef('');

  useEffect(() => {
    if (!onPageFilters) return;
    const pageFilters = CoursesData?.getCourseList?.pageFilters;
    const next = pageFilters ? JSON.stringify(pageFilters) : '';
    if (next !== lastPageFiltersStringRef.current) {
      lastPageFiltersStringRef.current = next;
      onPageFilters(pageFilters || null);
    }
  }, [CoursesData?.getCourseList?.pageFilters, onPageFilters]);

  useEffect(() => {
    if (!onPageTags) return;
    const pageTags = CoursesData?.getCourseList?.tagFilters;
    const next = pageTags ? JSON.stringify(pageTags) : '';
    if (next !== lastPageTagsStringRef.current) {
      lastPageTagsStringRef.current = next;
      onPageTags(pageTags || null);
    }
  }, [CoursesData?.getCourseList?.tagFilters, onPageTags]);

  const filteredCourses = useMemo(() => {
    if (queryPareamsTag === 'all' || queryPareamsTag === '') {
      return CoursesData?.getCourseList?.courses;
    }
    const filtered = allCourses.filter((course) => course.tags?.some((t) => t.id === queryPareamsTag));
    return filtered;
  }, [queryPareamsTag, allCourses, clearFilter]);

  // Load more courses
  const loadMoreCourses = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      const currentFilters = debouncedSearchKey.length >= 2
        ? [{ key: 'name', value: debouncedSearchKey }]
        : filters;

      const { data } = await fetchMore({
        variables: {
          filters: currentFilters,
          page: nextPage,
          limit: 12,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getCourseList?.courses) return prev;

          return {
            getCourseList: {
              ...fetchMoreResult.getCourseList,
              courses: [...prev.getCourseList.courses, ...fetchMoreResult.getCourseList.courses],
            },
          };
        },
      });

      if (data?.getCourseList?.courses) {
        setAllCourses((prevCourses) => {
          const newCourses = [...prevCourses, ...data.getCourseList.courses];

          return newCourses;
        });
        setCurrentPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more courses:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasNextPage, isLoadingMore, fetchMore, filters, debouncedSearchKey]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore) {
          loadMoreCourses();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px', // Trigger 100px before reaching the element
      },
    );

    if (loadingElement) {
      observer.observe(loadingElement);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasNextPage, isLoadingMore, loadMoreCourses, allCourses, loadingElement]);

  // Handle search (this should clear and refetch)
  useEffect(() => {
    if (debouncedSearchKey.length >= 2) {
      const searchFilters = [{ key: 'name', value: debouncedSearchKey }];
      setFilters(searchFilters);
      setCurrentPage(1);
      setAllCourses([]);

      trackEvent(COURSES_SEARCH_INITIATED, {
        searchQuery: `name=${debouncedSearchKey}`,
      });

      if (Object.values(queryPareams).filter((value) => value !== '').length > 0) {
        clearFilterafterSeacrh();
      } else {
        setSelectedTag(''); // update state 'all' to empty ''
        router.replace(router.pathname, undefined, { shallow: true });
        // window.history.pushState(null, '', window.location.origin + window.location.pathname);
      }

      // Refetch with search filters
      refetch({ filters: searchFilters, page: 1, limit: 12 });
    } else if (debouncedSearchKey.length === 0) {
      setFilters(filterArray);
      setCurrentPage(1);
      refetch({ filters: filterArray, page: 1, limit: 12 });
    }
  }, [debouncedSearchKey]);

  // Handle URL parameter changes (mode, language, centreId, examId)
  useEffect(() => {
    const currentFiltersString = JSON.stringify(filterArray);
    const hasUrlFilterChanged = currentFiltersString !== lastFiltersString;

    if (hasUrlFilterChanged && debouncedSearchKey.length < 2) {
      setFilters(filterArray);
      setCurrentPage(1);
      setAllCourses([]);
      setLastFiltersString(currentFiltersString);

      refetch({ filters: filterArray, page: 1, limit: 12 });
    }
  }, [filterArray, debouncedSearchKey, refetch, lastFiltersString]);

  // Handle filter changes (for other filter triggers like isRefetch, clearFilter)
  useEffect(() => {
    if (isRefetch || clearFilter) {
      setCurrentPage(1);
      setAllCourses([]);

      const currentFilters = debouncedSearchKey.length >= 2
        ? [{ key: 'name', value: debouncedSearchKey }]
        : filterArray;

      refetch({ filters: currentFilters, page: 1, limit: 12 });
    }
  }, [isRefetch, clearFilter, refetch, filterArray, debouncedSearchKey]);

  if (loading || searchLoad) {
    return (
      <div className="grid lg:grid-cols-3 grid-cols-1 gap-6">
        {[...Array(5)].map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <CardSkeleton key={index} />
        ))}
      </div>
    );
  }
  if (searchKey.length > 1 && debouncedSearchKey.length < 2) {
    return (
      <div className="flex justify-center items-center">
        <Spinner color="text-customBlue" />
      </div>
    );
  }

  if (error) {
    const statusCode = error?.networkError?.statusCode || error?.networkError?.response?.status;
    if (statusCode === 401) {
      clearUserSession();
      return null;
    }
    return (
      <p className="text-red-500">
        Error: Something went wrong
      </p>
    );
  }

  const typeClasses = {
    Regular: 'bg-[#E3EAFF] text-[#002FFF]',
    Online: 'bg-[#DFF4CE] text-[#264909]',
    Weekend: 'bg-[#FFEFCC] text-[#AA6311]',
  };

  const handleRedirect = (slug, course) => {
    trackEvent(COURSE_CARD_CLICKED, {
      courseId: course?.id,
      courseName: course?.name,
      type: course?.type,
      duration: course?.duration,
      screenName: 'Library',
    });
    if (!user?.id) {
      const encodeUrl = encodeURIComponent(
        `${domainBaseUrl()}/prep/course/${slug}`,
      );
      return `/course/${slug}?redirectTo=${encodeUrl}`;
    }
    return `/course/${slug}`;
  };
  const handleClick = (course) => {
    trackEvent(COURSE_CARD_CLICKED, {
      courseId: course?.id,
      courseName: course?.name,
      type: course?.type,
      duration: course?.duration,
      screenName: 'Library',
    });
  };

  return (
    <div>

      {filteredCourses?.length > 0 ? (
        <>
          <div>
            {/* <pre>{JSON.stringify(filteredCourses, null, 2)}</pre> */}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <Link href={handleRedirect(course?.slug)} key={`${course?.id}}`} onClick={() => handleClick(course)} lang={course?.language === 'Hindi' ? 'hi' : undefined}>
                <div className="cursor-pointer rounded-lg overflow-hidden bg-white border border-[#B8E5FF] p-2">
                  <div className="relative">
                    <span className={`${typeClasses[course?.type] || ''} inline-flex absolute items-center text-xs font-light rounded right-2.5 top-2.5 px-1 py-1`}>
                      {course?.type}
                    </span>
                    <Image
                      width={500}
                      height={150}
                      className="w-full rounded-md aspect-[2/1] lg:h-[161px]"
                      src={course?.cardImage}
                      alt="CardImage"
                    />
                  </div>
                  <div className="lg:p-3 p-2 lg:h-48 overflow-hidden">
                    <span className="items-center mb-2 text-sm font-medium rounded me-1" style={{ background: '#E9F3FF', color: '#217ABB', padding: '3px 8px' }}>
                      {course?.exam?.name}
                    </span>
                    <h3 className="lg:text-lg text-sm font-semibold text-gray-800 line-clamp-2 leading-6">
                      {course?.name}
                    </h3>
                    <p className="text-greyFont font-light mt-2 line-clamp-3 lg:text-base text-sm">
                      {course?.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && (
            <div className="flex justify-center items-center mt-6 mb-4">
              <Spinner color="text-customBlue" />
              <span className="ml-2 text-gray-600">Loading more courses...</span>
            </div>
          )}

          {/* Intersection observer target */}
          {hasNextPage && (
            <div
              ref={setLoadingElement}
              className="h-20 flex justify-center items-center mt-6 mb-16 lg:mb-4"
            >
              <p className="text-gray-400 text-sm">Scroll to load more...</p>
            </div>
          )}

          {!hasNextPage && allCourses.length > 0 && (
            <div className="h-10 flex justify-center items-center mt-4 mb-16 lg:mb-4">
              <p className="text-gray-500 text-sm">No more courses to load</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            {/* <pre>{JSON.stringify(filteredCourses, null, 2)}</pre> */}
          </div>
          <NoDataFound noMsg={`${searchKey.length >= 2 ? 'No results found. Please try different keywords.' : 'No courses match your selected filters. Please try adjusting your filters.'}`} />
        </>
      )}
    </div>
  );
}

CourseList.propTypes = {
  type: PropTypes.string,
  pageType: PropTypes.string,
  onPageTags: PropTypes.func,
  isRefetch: PropTypes.bool,
  queryPareams: PropTypes.object.isRequired,
  queryPareamsTag: PropTypes.string,
  clearFilter: PropTypes.bool,
  searchKey: PropTypes.string,
  clearFilterafterSeacrh: PropTypes.func.isRequired,
  setSelectedTag: PropTypes.func.isRequired,
  searchLoad: PropTypes.any,
  onPageFilters: PropTypes.func,
  user: PropTypes.any,
};

export default React.memo(CourseList);
